import { useRouter } from "next/router"
import React, { useMemo } from "react"

import Icon from "~/components/atoms/Icon"
import Typography from "~/components/atoms/Typography"
import { Color } from "~/consts/style-mixins"
import { range } from "~/utils"

import { PaginationLink } from "./_PaginationLink"
import * as Styled from "./styled"

export interface Props {
  baseUrl?: string
  queryStrings?: string
  currentPage: number
  maxPage: number
  maxCount: number
  currentMin: number
  currentMax: number
  onPageCountClick?: (pageCount: number) => void
}

const BASE_PAGE_COUNT = 5

const Pagination = (props: Props) => {
  const { queryStrings, currentPage, maxPage, maxCount, currentMin, currentMax, onPageCountClick } = props
  const router = useRouter()
  const baseUrl = useMemo(() => props.baseUrl || router.pathname, [props.baseUrl, router])

  const paginationLinkProps = useMemo(
    () => ({ baseUrl, queryStrings, onPageCountClick }),
    [baseUrl, queryStrings, onPageCountClick]
  )

  const renderedCenterNumbers = useMemo(() => {
    switch (true) {
      // 1~5まで羅列する
      case currentPage <= BASE_PAGE_COUNT && maxPage <= BASE_PAGE_COUNT:
        return (
          <>
            {range(Math.min(maxPage, BASE_PAGE_COUNT)).map((i) => (
              <PaginationLink key={i} {...paginationLinkProps} page={i + 1} isCurrent={currentPage === i + 1} />
            ))}
          </>
        )

      // currentPageが5以下であれば1~5羅列し、maxPageを表示
      case currentPage <= BASE_PAGE_COUNT:
        return (
          <>
            {range(BASE_PAGE_COUNT).map((i) => (
              <PaginationLink key={i} {...paginationLinkProps} page={i + 1} isCurrent={currentPage === i + 1} />
            ))}
            <div className="dot">
              <Icon type="Leader" size={12} color={Color.LightGray} />
            </div>
            <PaginationLink key={maxPage} {...paginationLinkProps} page={maxPage} />
          </>
        )

      // currentPageが6~,(最大-5)~最大なら、1を表示し、(最大-5)からmaxPageを羅列
      case 6 <= currentPage && maxPage - BASE_PAGE_COUNT < currentPage:
        return (
          <>
            <PaginationLink {...paginationLinkProps} page={1} />
            <div className="dot">
              <Icon type="Leader" size={12} color={Color.LightGray} />
            </div>
            {range(BASE_PAGE_COUNT).map((i) => {
              const nowPageCount = maxPage - BASE_PAGE_COUNT + i + 1
              return (
                <PaginationLink
                  key={i}
                  {...paginationLinkProps}
                  page={nowPageCount}
                  isCurrent={currentPage === nowPageCount}
                />
              )
            })}
          </>
        )
      // 1,maxPageを両端に表示し、currentPage前後2ページ分を羅列
      default:
        return (
          <>
            <PaginationLink {...paginationLinkProps} page={1} />
            <div className="dot">
              <Icon type="Leader" size={12} color={Color.LightGray} />
            </div>
            {range(BASE_PAGE_COUNT).map((i) => {
              const nowPageCount = currentPage + (-2 + i)
              return (
                <PaginationLink
                  key={i}
                  {...paginationLinkProps}
                  page={nowPageCount}
                  isCurrent={currentPage === nowPageCount}
                />
              )
            })}
            <div className="dot">
              <Icon type="Leader" size={12} color={Color.LightGray} />
            </div>
            <PaginationLink key={maxPage} {...paginationLinkProps} page={maxPage} />
          </>
        )
    }
  }, [baseUrl, queryStrings, currentPage, maxPage, maxCount])

  return (
    <Styled.Container>
      <div className="title">
        <Typography type="Label" colorProp={Color.Black}>
          {maxCount}件中
        </Typography>
        <div className="display-wrapper">
          <Typography type="Description" lineHeightProp={140}>
            {currentMin}〜{currentMax}件を表示
          </Typography>
        </div>
      </div>
      <div className="pagination">
        {/* 戻るボタン */}
        <PaginationLink
          {...paginationLinkProps}
          page={currentPage - 1}
          disabled={currentPage === 1}
          icon={<Icon type="Back" size={12} color={Color.LightGray} />}
        />

        {renderedCenterNumbers}

        {/* 次へボタン */}
        <PaginationLink
          {...paginationLinkProps}
          page={currentPage + 1}
          disabled={currentPage === maxPage}
          icon={<Icon type="Next" size={12} color={Color.LightGray} />}
        />
      </div>
    </Styled.Container>
  )
}

export default Pagination
